import type { ActiveAnalysis } from "../api/useActiveAnalysesStream";
import type { FailureValueStatus, IndexedFailureMetadata } from "../api/useFailureMetadata";
import type { IndexedFailureValues } from "../api/useIndexedFailureValues";
import type { Setup } from "../api/useSetups";
import { inv } from "../lib/chiSquared";
import computeUnexplainedGap from "./computeUnexplainedGap";
import type { MetrologyOverrides } from "../api/useMetrologyOverrides";
import type { QuantityIndex } from "../api/useQuantities";
import { isAnalysisQuantityValid } from "./metrology";
const emptyObject = Object.freeze({});
export default function computeRemainingSymptom(
  analysis: ActiveAnalysis,
  indexedFailureValues: IndexedFailureValues,
  setup: Setup | null,
  status: Set<FailureValueStatus>,
  _metrologyOverrides: MetrologyOverrides,
  indexedFailureMetadata: IndexedFailureMetadata,
  quantityIndex: QuantityIndex,
  offsetByQuantityIdAndSetupId: Map<string /*setupId*/, Map<string /*quantityId*/, number>>,
): number {
  if (setup === null) return 0;
  let count = 0;
  const norm = setup.diagnosisInputs.reduce<number>((acc, obs) => {
    if (!obs.enabled) return acc;
    const quantity = quantityIndex[obs.quantityId];
    if (quantity === undefined) return acc;
    const quantityValue = analysis.quantityValues[obs.quantityId];
    if (quantityValue === undefined || quantityValue.simulated === null) return acc;
    if (!isAnalysisQuantityValid(analysis, quantity)) return acc;

    const offset = offsetByQuantityIdAndSetupId.get(setup.id)?.get(obs.quantityId) ?? 0;
    const unexplainedGap = computeUnexplainedGap(
      analysis,
      indexedFailureValues,
      indexedFailureMetadata,
      emptyObject,
      obs.quantityId,
      status,
      offset,
    );

    if (unexplainedGap !== null) {
      count++;
      return acc + Math.pow(unexplainedGap / obs.sigma, 2);
    }

    return acc;
  }, 0);

  return Math.sqrt(norm / inv(0.95, count)) * -1;
}
